import React, { useEffect } from 'react';
import { SearchX } from 'lucide-react';
import { RawShopEntry } from '../../utils/fortniteShopParser';
import { ItemCard } from './ItemCard';
import { CatalogSkeleton } from './CatalogSkeleton';
import { SupportedCurrency } from '../../types';

export function balanceEntries<T extends { isBundle?: boolean; category?: string }>(items: T[]): (T & { isOrphan: boolean })[] {
  const queue = [...items];
  const balanced: (T & { isOrphan: boolean })[] = [];

  while (queue.length > 0) {
    const current = queue.shift()!;
    const is2Col = Boolean(current.isBundle || current.category === 'LOTES');

    if (is2Col) {
      balanced.push({ ...current, isOrphan: false });
    } else {
      const pairIdx = queue.findIndex((o) => !o.isBundle && o.category !== 'LOTES');
      if (pairIdx !== -1) {
        const pair = queue.splice(pairIdx, 1)[0];
        balanced.push({ ...current, isOrphan: false });
        balanced.push({ ...pair, isOrphan: false });
      } else {
        balanced.push({ ...current, isOrphan: true });
      }
    }
  }

  return balanced;
}

export interface CatalogGridProps {
  sections: [string, RawShopEntry[]][];
  isLoading?: boolean;
  currency?: SupportedCurrency;
  cartOfferIds?: Set<string>;
  onToggleCart?: (entry: RawShopEntry) => void;
  onOpenDetail?: (entry: RawShopEntry) => void;
  onResetFilters?: () => void;
}

export function getSectionColor(entries: RawShopEntry[]): string {
  if (!entries || entries.length === 0) return '#1447e6';
  for (const h of entries) {
    const rawColor =
      (h.colors as any)?.color1 ||
      (h.colors as any)?.Color1 ||
      (h.colors as any)?.color2 ||
      (h.colors as any)?.Color2 ||
      (h.colors as any)?.color3 ||
      (h.colors as any)?.Color3 ||
      (h as any).newDisplayAsset?.materialInstances?.[0]?.colors?.Background_Color_B ||
      (h as any).newDisplayAsset?.materialInstances?.[0]?.colors?.Background_Color_A;
    if (rawColor && typeof rawColor === 'string') {
      const c = rawColor.replace(/^#/, '').trim();
      if (c.length >= 6) return `#${c.substring(0, 6)}`;
    }
  }
  return '#1447e6';
}

export const CatalogGrid: React.FC<CatalogGridProps> = ({
  sections,
  isLoading = false,
  currency = 'ARS',
  cartOfferIds = new Set(),
  onToggleCart,
  onOpenDetail,
  onResetFilters,
}) => {
  // Manejar navegación directa a skin desde el carrusel de la landing
  useEffect(() => {
    if (isLoading || sections.length === 0) return;

    let targetName = '';
    let targetId = '';

    try {
      const stored = sessionStorage.getItem('gf_target_skin');
      if (stored) {
        const parsed = JSON.parse(stored);
        targetName = parsed.name || '';
        targetId = parsed.id || '';
        sessionStorage.removeItem('gf_target_skin');
      }
    } catch {}

    if (!targetName && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      targetName = params.get('skin') || '';
    }

    if (!targetName && !targetId) return;

    const cleanTargetName = targetName.toLowerCase().trim();
    const cleanTargetId = targetId.toLowerCase().trim();

    const timer = setTimeout(() => {
      const cards = Array.from(
        document.querySelectorAll<HTMLElement>('.shop-item-card-wrapper')
      );
      let targetEl: HTMLElement | null = null;

      let exactSkinMatch: HTMLElement | null = null;
      let nonBundleMatch: HTMLElement | null = null;
      let fallbackMatch: HTMLElement | null = null;

      if (cleanTargetName) {
        for (const el of cards) {
          const name = (el.getAttribute('data-item-name') || '').toLowerCase().trim();
          const isBundle =
            el.getAttribute('data-is-bundle') === 'true' ||
            name.startsWith('lote ') ||
            name.includes('lote de');

          // 1. Coincidencia exacta con la skin (no lote)
          if (name === cleanTargetName && !isBundle) {
            exactSkinMatch = el;
            break;
          }
          if (name === cleanTargetName && !exactSkinMatch) {
            exactSkinMatch = el;
          }
          // 2. Coincidencia parcial con la skin (no lote)
          if (!isBundle && (name.includes(cleanTargetName) || cleanTargetName.includes(name))) {
            if (!nonBundleMatch) nonBundleMatch = el;
          }
          // 3. Coincidencia general de respaldo
          if (name.includes(cleanTargetName) || cleanTargetName.includes(name)) {
            if (!fallbackMatch) fallbackMatch = el;
          }
        }
        targetEl = exactSkinMatch || nonBundleMatch || fallbackMatch;
      }

      if (!targetEl && cleanTargetId) {
        targetEl =
          cards.find((el) => {
            const id = (el.getAttribute('data-item-id') || '').toLowerCase();
            return id === cleanTargetId;
          }) || null;
      }

      if (targetEl) {
        // Scroll suave hacia la tarjeta de la skin
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Cuando termina el scroll, activar animación que introduce el hover
        let handled = false;
        const triggerHoverIntro = () => {
          if (handled) return;
          handled = true;
          window.removeEventListener('scrollend', triggerHoverIntro);
          targetEl?.classList.add('target-hover-intro');
          setTimeout(() => {
            targetEl?.classList.remove('target-hover-intro');
          }, 2800);
        };

        window.addEventListener('scrollend', triggerHoverIntro, { once: true });
        // Fallback si el navegador no soporta o retrasa el evento scrollend
        setTimeout(triggerHoverIntro, 750);
      }
    }, 180);

    return () => clearTimeout(timer);
  }, [isLoading, sections]);

  if (isLoading) {
    return <CatalogSkeleton count={12} />;
  }

  if (sections.length === 0) {
    return (
      <div className="text-center py-20 bg-white/80 rounded-2xl border-2 border-dashed border-[#cfd7e3] mt-8 max-w-2xl mx-auto p-8 shadow-sm">
        <div className="w-14 h-14 rounded-2xl bg-blue-600/10 border border-blue-600/20 flex items-center justify-center mx-auto mb-4 text-[#1447e6]">
          <SearchX className="w-7 h-7" />
        </div>
        <h3 className="text-xl md:text-2xl font-black text-[#06173f] uppercase italic tracking-wider font-chakra">
          No se encontraron cosméticos
        </h3>
        <p className="text-[#475569] text-sm mt-2 max-w-md mx-auto">
          Prueba con otra búsqueda para explorar la tienda de hoy.
        </p>
        {onResetFilters && (
          <button
            type="button"
            onClick={onResetFilters}
            className="mt-5 inline-flex items-center gap-2 px-6 py-2.5 bg-[#1447e6] hover:bg-blue-700 text-white font-chakra font-black uppercase italic tracking-wider text-xs md:text-sm rounded-xl transition-all shadow-md active:scale-95 cursor-pointer"
          >
            <span>Ver todo el catálogo</span>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-14 md:space-y-24">
      {/* Secciones del Catálogo */}
      {sections.map(([sectionName, sectionEntries], sIdx) => {
        const subCategory = sectionEntries[0]?.layout?.category;
        const sectionColor = getSectionColor(sectionEntries);

        return (
          <section
            key={sectionName}
            id={`section-${sIdx}`}
            className="shop-section scroll-mt-32 md:scroll-mt-40 pt-4 sm:pt-6 md:pt-8"
          >
            {/* Encabezado oficial con subtítulo azul y cápsula rounded-full fiel a la tienda */}
            <div className="mb-4 md:mb-6">
              {subCategory && (
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] md:text-xs font-black uppercase tracking-[0.22em] text-[#1447e6] font-chakra">
                    {subCategory}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-3 md:gap-4 min-w-0">
                <span
                  className="w-2.5 md:w-3 h-7 md:h-8 rounded-full shadow-[2px_2px_0_rgba(6,23,63,0.35)] shrink-0"
                  style={{ backgroundColor: sectionColor }}
                />
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-black uppercase italic tracking-tight text-[#06173f] font-chakra break-words line-clamp-2 leading-tight">
                  {sectionName}
                </h2>
                <div className="h-px bg-gradient-to-r from-[#06173f]/30 via-[#06173f]/10 to-transparent flex-1 ml-2 min-w-4" />
              </div>
            </div>

            {/* Grilla oficial con proporciones y orden fiel de Fortnite (2 columnas móvil, 4 columnas desktop) */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-3 md:gap-x-5 gap-y-10 md:gap-y-16">
              {sectionEntries.map((entry) => (
                <ItemCard
                  key={entry.offerId}
                  entry={entry}
                  currency={currency}
                  inCart={cartOfferIds.has(entry.offerId)}
                  onToggleCart={onToggleCart}
                  onOpenDetail={onOpenDetail}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default CatalogGrid;
